/**
 * A delicate constellation motif for the Welcome sheet.
 *
 * Replaces the small crescent that used to sit above "Welcome to Astral" — the
 * composition already carries the large Columbina crescent, and two moon motifs
 * on one screen competed with each other. This is deliberately the same visual
 * family as the app's stardust: one bright four-point sparkle, a few smaller
 * stars at varying brightness, tiny dots, and faint implied connecting lines.
 *
 * The layout is hand-placed and asymmetric so it reads as a fragment of sky
 * rather than a centred icon. Colours come from the existing Astral ramp:
 * pearl core, blush mid-tones, dusty-rose falloff.
 */

/** Four-point sparkle path, centred on (0,0) and scaled by `r`. */
function star(cx: number, cy: number, r: number): string {
  const w = r * 0.26; // waist — smaller value = sharper points
  return [
    `M${cx} ${cy - r}`,
    `Q${cx + w} ${cy - w} ${cx + r} ${cy}`,
    `Q${cx + w} ${cy + w} ${cx} ${cy + r}`,
    `Q${cx - w} ${cy + w} ${cx - r} ${cy}`,
    `Q${cx - w} ${cy - w} ${cx} ${cy - r}`,
    'Z',
  ].join(' ');
}

/** Secondary stars: asymmetric, varied in size and brightness. */
const STARS = [
  { x: 26, y: 26, r: 5.5, op: 0.85 },
  { x: 84, y: 20, r: 4, op: 0.7 },
  { x: 98, y: 52, r: 3, op: 0.55 },
  { x: 18, y: 62, r: 3.4, op: 0.6 },
  { x: 62, y: 74, r: 4.6, op: 0.75 },
];

/** Tiny dust points — no glow, just presence. */
const DOTS = [
  { x: 40, y: 14, r: 1.1, op: 0.7 },
  { x: 108, y: 34, r: 0.9, op: 0.5 },
  { x: 8, y: 42, r: 1, op: 0.55 },
  { x: 46, y: 90, r: 0.85, op: 0.45 },
  { x: 90, y: 84, r: 1.15, op: 0.6 },
  { x: 72, y: 44, r: 0.8, op: 0.5 },
];

/** Implied constellation lines — barely-there, never a full star map. */
const LINES = [
  'M26 26 L58 48',
  'M58 48 L84 20',
  'M58 48 L62 74',
  'M18 62 L26 26',
];

export function ConstellationMark(): JSX.Element {
  return (
    <svg className="constellation" viewBox="0 0 128 104" fill="none" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id="constHalo" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="var(--neb-pearl)" stopOpacity="0.5" />
          <stop offset="55%" stopColor="var(--neb-dusty-rose)" stopOpacity="0.16" />
          <stop offset="100%" stopColor="var(--neb-dusty-rose)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Restrained dusty-rose bloom behind the primary sparkle. */}
      <ellipse cx="58" cy="48" rx="42" ry="34" fill="url(#constHalo)" />

      <g className="constellation__lines">
        {LINES.map((d, i) => (
          <path key={i} d={d} stroke="var(--neb-dusty-rose)" strokeWidth="0.6" strokeOpacity="0.3" />
        ))}
      </g>

      {DOTS.map((d, i) => (
        <circle key={`d${i}`} cx={d.x} cy={d.y} r={d.r} fill="var(--neb-pearl)" opacity={d.op} />
      ))}

      {STARS.map((s, i) => (
        <path key={`s${i}`} className="constellation__star" d={star(s.x, s.y, s.r)} fill="#FFE9F3" opacity={s.op} />
      ))}

      {/* The centrepiece: crisp near-white core with a tight pearl halo. */}
      <path className="constellation__hero" d={star(58, 48, 20)} fill="#FFFFFF" />
    </svg>
  );
}
